import React, { useEffect } from "react";
import {
  AlertDialogAction,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogContent,
  AlertDialogTrigger,
  AlertDialog,
  Button,
} from "@/components/ui";
import { useMutation } from "@apollo/client";
import { toast } from "sonner";
import { DESTROY_LIVE_EVENT_MUTATION } from "@/api/mutations/destroyLiveEvent";
import { LiveEvent } from "@/__generated__/graphql";
import { useTranslation } from "react-i18next";
import { ORGANIZATION_LIVE_EVENTS_QUERY } from "@/api/queries/orgLiveEvents";

export function DestroyEventButton({ event }: { event: LiveEvent }) {
  const { t } = useTranslation();

  const [destroyLiveEventMutation, { data }] = useMutation(
    DESTROY_LIVE_EVENT_MUTATION,
    { refetchQueries: [ORGANIZATION_LIVE_EVENTS_QUERY] },
  );

  const handleDeleteEvent = (event: LiveEvent) => {
    const { id } = event;
    if (id) {
      destroyLiveEventMutation({
        variables: { liveEventId: id },
      });
    }
  };

  useEffect(() => {
    if (data?.destroyLiveEventMutation?.success) {
      toast.success(
        t("gamesAndLiveEvents.table.scheduled.confirmDialogs.delete.success"),
      );
    } else if (data?.destroyLiveEventMutation?.errors?.length) {
      const { errors } = data?.destroyLiveEventMutation ?? {};
      toast.error(
        t("gamesAndLiveEvents.table.scheduled.confirmDialogs.delete.error"),
        {
          description: errors?.map(({ message }) => message).join(", "),
        },
      );
    }
  }, [data, t]);

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className="h-auto w-full justify-start p-0 font-normal"
        >
          {t("gamesAndLiveEvents.table.scheduled.actions.delete")}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(
              "gamesAndLiveEvents.table.scheduled.confirmDialogs.delete.title",
            )}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(
              "gamesAndLiveEvents.table.scheduled.confirmDialogs.delete.message",
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("global.cancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={() => handleDeleteEvent(event)}>
            {t("global.yes")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
